import tenantConstants from '@constants';
import { t } from 'i18next';
import * as yup from 'yup';
import { strings } from '../../../constants/strings';
import { skipFieldsForField } from './listing-form-data';
import { regex } from '../../../constants/regex';
import { isKeycloakDisabledForPath } from '@/utility/helpers';

// const englishRegex = /^[A-Za-z0-9\s.,|`~\\"/'\[\]{}<>:?!@#$%^&*()_+=;-]*$/;
// const englishRegex = /^[^\u0600-\u06FF]+$/;
// const arabicRegex = /^(?![-\s])[\u0600-\u06FF()0-9\s.,\-:|"'";@#$%^&*+}{}?()=<>_!/]*[\u0600-\u06FF0-9\s.,\-:|"'";@#$%^&*+}{}?()=<>_!/]*$/;
// const arabicRegex = /^[^A-Za-z]+$/;
export const getPostListingValidationSchema = (
  locale,
  purposeSlug,
  type_id,
  is_mobile_verified,
  is_offplan_listing,
  domain_name,
  autoGenerateTitle,
  autoGenerateDescription,
) => {
  const isSellOrRentProperty = purposeSlug === 'sale' || purposeSlug === 'rent';
  const isDailyRental = purposeSlug === 'daily-rental';
  const isOffPlan = is_offplan_listing;
  const imagesRequired = isKeycloakDisabledForPath()
  return yup.object().shape({
    bedrooms: yup.mixed().when(['sub_listing_type', 'property_type'], ([sub_listing_type, property_type], schema) => {
      const shouldRequire = !(
        skipFieldsForField?.['bedrooms'].property_type?.[property_type || sub_listing_type?.id || type_id]?.skipField ||
        skipFieldsForField?.['bedrooms'].property_type?.[property_type || sub_listing_type?.id || type_id]?.isOptional
      );

      return shouldRequire
        ? yup.string().required(t('Please select number of bedrooms')).typeError(t('Please select number of bedrooms'))
        : schema.notRequired();
    }),
    property_type: yup.string().required(t('Please select property type')).typeError(t('Please select property type')),
    property_images: imagesRequired ? yup
      .array()
      .min(1, t('No Images Uploaded'))
      .required(t('Invalid image')) : null,
    bathrooms: yup.mixed().when(['sub_listing_type', 'property_type'], ([sub_listing_type, property_type], schema) => {
      const shouldRequire = !(
        skipFieldsForField?.['bathrooms'].property_type?.[property_type || sub_listing_type?.id || type_id]
          ?.skipField ||
        skipFieldsForField?.['bathrooms'].property_type?.[property_type || sub_listing_type?.id || type_id]?.isOptional
      );

      return shouldRequire
        ? yup
            .string()
            .required(t('Please select number of bathrooms'))
            .typeError(t('Please select number of bathrooms'))
        : schema.notRequired();
    }),
    videos: yup.array().of(
      yup
        .object()
        .nullable()
        .test({
          message: t('Please enter a valid url'),
          test: (value) => {
            if (!value.hasOwnProperty('_destroy') || !value.hasOwnProperty('url')) {
              return regex.youtubeUrl.test(value?.url);
            } else {
              return true;
            }
          },
        }),
    ),
    ...(!is_mobile_verified && {
      mobile: yup
        .string()
        .required(t('Please enter your number'))
        .test('format', t(strings.im.phone), (value) => {
          if (!value) return true;
          return tenantConstants.PHONE_REGEX ? tenantConstants.PHONE_REGEX.test(value) : true;
        })
        .nullable(),
    }),
    ...(isSellOrRentProperty && {
      furnished: yup.mixed().when('sub_listing_type', (sub_listing_type, schema) => {
        const shouldRequire = !(
          skipFieldsForField?.['furnished'].property_type?.[sub_listing_type?.id || type_id]?.skipField ||
          skipFieldsForField?.['furnished'].property_type?.[sub_listing_type?.id || type_id]?.isOptional
        );

        return shouldRequire
          ? yup
              .string()
              .required(t('Please select a value for furnished'))
              .typeError(t('Please select a value for furnished'))
          : schema.notRequired();
      }),
    }),
    ...(isOffPlan && {
      completion_status: yup
        .string()
        .required(t('Please select a value for completion status'))
        .typeError(t('Please select a value for completion status')),
    }),
    ...((purposeSlug == 'sale' || isDailyRental) && {
      price: yup
        .number()
        .required(t('Please enter price'))
        .moreThan(0, t('Invalid Price'))
        .typeError(t('Invalid Price')),
    }),

    ...((isDailyRental || isOffPlan) && {
      area: yup
        .number()
        .required(t('Please enter area'))
        .typeError(t('Invalid Area'))
        .test('is-integer-string', t('Invalid Area'), (val) => {
          const stringValue = val?.toString();
          return /^[0-9]+$/.test(stringValue);
        }),
    }),

    ...(isDailyRental && {
      national_address: yup
        .string()
        .nullable()
        .notRequired()
        .test('len', t('Please enter a valid National Address'), (value) => !value || value.length == 8)
        .matches(/^[A-Z]{4}[0-9]{4}$/, t('Please enter a valid National Address')),
      redirection_link: yup
        .string()
        .nullable()
        .test('regex', t('Enter a valid website address'), (value) => {
          return !value ? true : value.match(/^((https?):\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+((\/|\?).*)?$/);
        })
        .test('match-domain', t('The domain in this URL does not match the approved domain for the user.'), (value) => {
          if (!value) return true;
          return (
            value
              .replace(/^https?:\/\//, '')
              .replace(/^www\./, '')
              .split('/')[0] === domain_name.replace(/^www\./, '')
          );
        }),
    }),

    ...(purposeSlug == 'rent' && {
      rental_frequency: yup.number().required(t('Please select rental frequency')).nullable(),
      rental_price: yup
        .number()
        .required(t('Please enter price'))
        .moreThan(0, t('Invalid Price'))
        .typeError(t('Invalid Price')),
    }),

    listing_type: yup.object().required(t('Please select listing type')).typeError(t('Please select listing type')),

    ...(!isDailyRental && {
      sub_listing_type: yup.mixed().when('listingTypes', (listingTypes, schema) => {
        return listingTypes.length ? yup.object().required(t('Please select property type')) : schema.notRequired();
      }),
    }),

    property_title_en:
      locale === 'en' || (locale === 'ar' && !autoGenerateTitle)
        ? yup
            .string()
            .required('Please enter property title')
            .test('len', 'Minimum of 5 characters required', (val) => !val || val.length >= 5)

            .test('contains-alpha', 'Title must contain at least one alphabetic character', (val) =>
              regex.onlyAlphabets.test(val),
            )
            .test('is-english', 'Please enter your title in English only', (val) => regex.englishRegex.test(val))
        : yup.string().nullable(),

    property_title_ar:
      locale === 'ar' || (locale === 'en' && !autoGenerateTitle)
        ? yup
            .string()
            .required('أدخل عنوان العقار بالعربية')
            .test('len', 'الحد الادنى لعدد الاحرف هو 5', (val) => !val || val?.length >= 5)
            .test('contains-alpha', 'يجب أن يحتوي الوصف على حرف أبجدي واحد على الأقل', (val) =>
              regex.onlyArabicChar.test(val),
            )
            .test('is-arabic', t('يرجى إدخال عنوان الأعلان بالعربي'), (val) => regex.arabicRegex.test(val))
        : yup.string().nullable(),

    property_description_ar:
      locale === 'ar' || (locale === 'en' && !autoGenerateDescription)
        ? yup
            .string()
            .required('أدخل وصف العقار بالعربية')
            .test('len', 'الحد الادنى للحروف 20 حرف', (val) => !val || val?.length >= 20)
        : yup.string().nullable(),

    property_description_en:
      locale === 'en' || (locale === 'ar' && !autoGenerateDescription)
        ? yup
            .string()
            .required('Please enter property description')
            .test('len', 'Minimum of 20 characters required', (val) => !val || val?.length >= 20)
            .test('contains-alpha', 'Description must contain at least one alphabetic character', (val) =>
              regex.onlyAlphabets.test(val),
            )
            .test('is-english', 'Please enter your description in English only', (val) => regex.englishRegex.test(val))
        : yup.string().nullable(),

    'location-info': yup
      .object({
        city: yup.object().required(t('City is required')),
      })
      .required(t('City is required')),
  });
};

export const getAdLicenseValidationSchema = (propertyPermissions = {}) => {
  const schema = {
    purpose: yup.string().required(t('Please select purpose')),
    property_type: yup.string().required(t('Please select property type')),
    property_sub_type: yup.object().required(t('Please select property sub type')),
    property_ownership_document_number: yup
      .string()
      .required(t('Please enter property ownership document number'))
      .matches(/^[0-9]{10,20}$/, t('Property ownership document number must be 10-20 digits only')),
    city: yup.object().required(t('City is required')),
    location: yup.object().required(t('Location is required')),
    price: yup
      .number()
      .required(t('Please enter price'))
      .positive(t('Price must be positive'))
      .typeError(t('Please enter a valid price')),
    name: yup.string().required(t('Please enter your name')),
    mobile: yup.string().required(t('Please enter your mobile number')),
    'location-info': yup
      .object({
        city: yup.object().required(t('City is required')),
        location: yup.object().required(t('Location is required')),
      })
      .required(t('Location is required'))
      .test('has-city-and-location', t('Both city and location are required'), (value) => {
        return value && value.city && value.location;
      }),
  };

  if (propertyPermissions.property_age) {
    schema.property_age = yup.object().required(t('Please select property age'));
  }

  if (propertyPermissions.bedrooms) {
    schema.bedrooms = yup.string().required(t('Please select number of bedrooms'));
  }

  if (propertyPermissions.area_size) {
    schema.area_size = yup
      .number()
      .required(t('Please enter area size'))
      .positive(t('Area size must be positive'))
      .typeError(t('Please enter a valid area size'));
  }

  return yup.object().shape(schema);
};
